import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { SearchBar } from './SearchBar';
import { cn } from '@/lib/utils';
import { useScrollHeader } from '@/hooks/useScrollHeader';

export const Header = () => {
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const scrolled = useScrollHeader(180);
  const { pathname } = useLocation();

  const navLinks = [
    { href: '/category/mobile-living', label: t('nav.mobileLiving') },
    { href: '/category/off-grid-homes', label: t('nav.offGridHomes') },
    { href: '/category/boat-living', label: t('nav.boatLiving') },
    { href: '/category/camping', label: t('nav.camping') },
    { href: '/glossary', label: t('nav.glossary') },
    { href: '/tools', label: t('nav.tools') },
    { href: '/cost-index', label: 'Cost Index' },
    { href: '/workbooks', label: 'Workbooks' },
  ];

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsMenuOpen(false);
      setIsSearchOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // Close the mobile panel when the viewport grows into the desktop layout.
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1280px)');
    const onChange = () => {
      if (mql.matches) setIsMenuOpen(false);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 bg-background/95 backdrop-blur-md transition-all duration-500 ease-editorial',
        scrolled ? 'border-b border-brass/20 shadow-sm' : 'border-b border-border/30',
      )}
    >
      <div className="container mx-auto px-6 md:px-8">
        <div
          className={cn(
            'flex items-center justify-between transition-[height] duration-500 ease-editorial',
            scrolled ? 'h-14 md:h-16' : 'h-16 md:h-20',
          )}
        >
          {/* Logo */}
          <Link
            to="/"
            className={cn(
              'font-display font-medium text-primary hover:text-primary/80 transition-all duration-500 tracking-wide whitespace-nowrap shrink-0',
              scrolled ? 'text-base sm:text-lg md:text-xl' : 'text-lg sm:text-xl md:text-2xl',
            )}
          >
            Modern Nomad Living
          </Link>

          {/* Desktop Navigation */}
          <nav
            className="hidden xl:flex items-center gap-6 2xl:gap-8 mx-auto px-6"
            aria-label="Main navigation"
          >
            {navLinks.map((link) => {
              const active =
                pathname === link.href || pathname.startsWith(`${link.href}/`);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'font-mono text-[10px] font-normal tracking-editorial uppercase whitespace-nowrap transition-colors duration-500 border-b pt-1 pb-1',
                    active
                      ? 'text-brass border-brass/60'
                      : 'text-foreground/70 border-transparent hover:text-brass',
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-1 md:gap-3 shrink-0">
            {/* Search toggle */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 text-foreground/50 hover:text-brass transition-colors duration-500"
              aria-label={t('nav.search')}
              aria-expanded={isSearchOpen}
              aria-controls="search-bar"
            >
              <Search className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 text-foreground/50 hover:text-brass transition-colors duration-500"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-nav"
            >
              {isMenuOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div
          id="search-bar"
          className={cn(
            'overflow-hidden transition-all duration-500 ease-in-out',
            isSearchOpen ? 'max-h-20 pb-5' : 'max-h-0'
          )}
        >
          {isSearchOpen && (
            <SearchBar onClose={() => setIsSearchOpen(false)} autoFocus />
          )}
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        id="mobile-nav"
        className={cn(
          'xl:hidden overflow-y-auto transition-all duration-500 ease-in-out bg-background border-b border-brass/15',
          isMenuOpen ? 'max-h-[75vh]' : 'max-h-0 overflow-hidden'
        )}
      >
        <nav className="container mx-auto px-6 py-2 flex flex-col" aria-label="Mobile navigation">
          {navLinks.map((link) => {
            const active =
              pathname === link.href || pathname.startsWith(`${link.href}/`);
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMenuOpen(false)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'font-mono text-[11px] font-normal tracking-editorial uppercase transition-colors duration-500 flex items-center min-h-[48px] border-b border-border/30 last:border-b-0',
                  active ? 'text-brass' : 'text-foreground/70 hover:text-brass',
                )}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </header>
  );
};
